import { useEffect, useState } from 'react';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { fetchAuthConfig, setTokenProvider } from './api.js';
import { Icon } from './components.jsx';

// The sign-in page renders before the app shell mounts (App is what normally stamps the theme
// classes onto <body>), so apply the saved theme here — a returning dark-mode user should get a
// dark sign-in page, not a white flash. App re-applies the same values when it mounts.
try {
  document.body.className ||=
    `theme-sky mode-${localStorage.getItem('msync-theme-mode')
      || (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')}`;
} catch { /* storage unavailable — :root fallback tokens (light + sky) apply */ }

// Module-level handle to the MSAL app + whether Entra sign-in is active, so the app shell (sidebar) can
// show identity + a real Sign-out without depending on the MSAL React context (which is absent in dev).
let _pca = null;
let _enabled = false;
export const isAuthEnabled = () => _enabled;
export function authSignOut() { if (_pca) _pca.logoutRedirect(); }

// Sign-in scaffold — local-first + dormant. On boot we ask the API (GET /api/auth-config) whether Entra
// sign-in is active. In local dev the API answers Mode=Dev, so we render the app straight through and its
// dev-stub authenticates every request — no identity provider, no login. In production (Mode=Entra with a
// SPA clientId) we spin up MSAL, gate the app behind sign-in, and hand api.js a bearer-token provider so
// every request carries the user's Entra access token. Nothing here activates until the API is configured
// for Entra, so it stays invisible on your machine.

// Landing/sign-in page — styled with the app's design tokens (theme.css) so it matches the ops
// console the user lands in, in whichever accent + light/dark mode they last used.
const page = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 16,
  background: 'var(--color-background)', color: 'var(--color-text)', fontFamily: 'var(--font-family-base)',
};
const card = {
  width: 360, maxWidth: '94vw', padding: '42px 36px 30px', textAlign: 'center',
  background: 'var(--color-surface)', border: '1px solid var(--color-border-light)',
  borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)',
};
const brandMark = {
  width: 54, height: 54, borderRadius: 14, background: 'var(--accent)',
  display: 'grid', placeItems: 'center', margin: '0 auto 16px',
};
const kicker = {
  fontFamily: 'var(--font-family-mono)', fontSize: 10, letterSpacing: '.14em', textTransform: 'uppercase',
  color: 'var(--color-muted)', marginBottom: 6,
};
const wordmark = { margin: 0, fontSize: 27, fontWeight: 600, letterSpacing: '-.01em', color: 'var(--color-text)' };
const tagline = { margin: '6px 0 0', fontSize: 14, color: 'var(--color-text-tertiary)' };
const signInBtn = {
  marginTop: 24, width: '100%', justifyContent: 'center', padding: '11px 20px', fontSize: 15,
};
const caption = { margin: '14px 0 0', fontSize: 12, color: 'var(--color-muted)' };

function SignInPage({ onSignIn, pending }) {
  return (
    <div style={page}>
      <div style={card}>
        <div style={brandMark}><Icon name="droplet" size={26} style={{ color: '#fff' }} /></div>
        <div style={kicker}>Operations Console</div>
        <h1 style={wordmark}>MSync</h1>
        <p style={tagline}>Water metering &amp; billing</p>
        <button className="btn btn--primary" style={signInBtn} onClick={onSignIn} disabled={pending}>Sign in</button>
        <p style={caption}>{pending ? 'Sign-in is being configured — check back soon' : 'You’ll be redirected to a secure sign-in page'}</p>
      </div>
    </div>
  );
}

export function AuthGate({ children }) {
  const [s, setS] = useState({ loading: true, enabled: false, pca: null, scope: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let cfg;
      try { cfg = await fetchAuthConfig(); }
      catch { cfg = { mode: 'Dev' }; }                       // API unreachable -> fail open to local/dev
      const enabled = cfg.mode === 'Entra' && !!cfg.clientId;
      if (!enabled) {
        // Entra mode with no SPA client id yet = a public deployment awaiting identity config.
        // Show the (inert) sign-in page rather than the app shell — the shell would render the
        // bundled mock dataset, which is more than an unauthenticated visitor should see.
        if (!cancelled) setS({ loading: false, enabled: false, pending: cfg.mode === 'Entra' });
        return;
      }

      const pca = new PublicClientApplication({
        auth: {
          clientId: cfg.clientId, authority: cfg.authority,
          redirectUri: window.location.origin, postLogoutRedirectUri: window.location.origin,
        },
        cache: { cacheLocation: 'sessionStorage' },
      });
      await pca.initialize();
      const redirect = await pca.handleRedirectPromise();     // finishes a sign-in we're returning from
      if (redirect?.account) pca.setActiveAccount(redirect.account);
      else if (!pca.getActiveAccount() && pca.getAllAccounts()[0]) pca.setActiveAccount(pca.getAllAccounts()[0]);

      const scope = cfg.scope;
      _pca = pca; _enabled = true;
      setTokenProvider(async () => {                          // api.js calls this for every request
        const account = pca.getActiveAccount();
        if (!account) return null;
        try { return (await pca.acquireTokenSilent({ account, scopes: [scope] })).accessToken; }
        catch (e) {
          if (e instanceof InteractionRequiredAuthError) await pca.acquireTokenRedirect({ scopes: [scope] });
          return null;
        }
      });
      if (!cancelled) setS({ loading: false, enabled: true, pca, scope });
    })();
    return () => { cancelled = true; };
  }, []);

  if (s.loading) return <div style={page}><span style={{ color: 'var(--color-muted)' }}>Loading…</span></div>;
  if (!s.enabled) {
    if (s.pending) return <SignInPage pending />;            // deployed, identity not configured yet
    // Local dev: straight through, no sign-in. #/signin-preview lets you eyeball the sign-in page
    // without an Entra-configured API (dev-only; with auth enabled the real gate takes precedence).
    if (window.location.hash === '#/signin-preview') return <SignInPage onSignIn={() => {}} />;
    return children;
  }
  return (
    <MsalProvider instance={s.pca}>
      <SignInGate scope={s.scope}>{children}</SignInGate>
    </MsalProvider>
  );
}

function SignInGate({ scope, children }) {
  const { instance } = useMsal();
  const isAuthed = useIsAuthenticated();
  if (!isAuthed) return <SignInPage onSignIn={() => instance.loginRedirect({ scopes: [scope] })} />;
  return children;                                           // identity + sign-out live in the app sidebar
}
